import { callExternalApi, clearCache } from "./external-api.service";
import { auth0, clearAuth0Cache } from "./auth0";

const API_SERVER_URL = import.meta.env.VITE_API_SERVER_URL;

export const createWorkspace = async (name, privacy) => {
  console.log("Called the createWorkspace method");

  const config = {
    url: `${API_SERVER_URL}/workspace`,
    method: "POST",
    data: {
      name,
      privacy,
    },
  };
  return await secureApiCallTemplate(config);
};

export const getUserWorkspaces = async () => {
  console.log("Called the getUserWorkspaces method");

  const config = {
    url: `${API_SERVER_URL}/workspace`,
    method: "GET",
  };
  return await secureApiCallTemplate(config);
};

// GET /workspace is answered from the API cache for up to five minutes, and
// creating, renaming or deleting a workspace doesn't invalidate it. Call this
// after one of those so the next read is fresh. Cache keys are
// `${method}-${url}-…`, so this matches the list and nothing under it.
export const clearUserWorkspacesCache = () =>
  clearCache(`GET-${API_SERVER_URL}/workspace-`);

// The user's default transcription language ({ primaryLanguage }, null means
// Auto-detect), shared with the mobile apps. Always read fresh: it can be
// changed on another device.
export const getPrimaryLanguage = async () =>
  secureApiCallTemplate(
    { url: `${API_SERVER_URL}/api/users/primary-language`, method: "GET" },
    true,
  );

// Pass null for Auto-detect, never "auto": the server would treat "auto" as a
// language code for every future file.
export const setPrimaryLanguage = async (primaryLanguage) =>
  secureApiCallTemplate({
    url: `${API_SERVER_URL}/api/users/primary-language`,
    method: "POST",
    data: { primaryLanguage },
  });

export const getAudioFiles = async (workspaceUUID, skipCache = false) => {
  console.log(
    "Called the getAudioFiles method",
    skipCache ? "- bypassing cache" : "",
  );

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file`,
    method: "GET",
  };
  return await secureApiCallTemplate(config, skipCache);
};

// Keyset-paginated page of the conversation list (infinite scroll).
// Returns { data: { items, pageInfo: { nextCursor, hasNextPage } } }.
export const getAudioFilesPage = async (
  workspaceUUID,
  {
    limit = 30,
    cursor = null,
    isFavorite = false,
    tagUUIDs = null,
    createdFrom = null,
    createdTo = null,
  } = {},
) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  if (isFavorite) params.set("isFavorite", "true");
  if (tagUUIDs?.length) params.set("tagUUIDs", tagUUIDs.join(","));
  if (createdFrom) params.set("createdFrom", createdFrom);
  if (createdTo) params.set("createdTo", createdTo);

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file?${params.toString()}`,
    method: "GET",
  };
  // Always fresh — pagination/cursor results must not be served from cache.
  return await secureApiCallTemplate(config, true);
};

export const getAudioFile = async (workspaceUUID, audioFileUUID) => {
  console.log("Called the getAudioFiles method");

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}`,
    method: "GET",
  };
  return await secureApiCallTemplate(config);
};

export const updateAudioFile = async (
  workspaceUUID,
  audioFileUUID,
  updateAudioFileDTO,
) => {
  console.log("Called the updateAudioFile method");

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}`,
    method: "PATCH",
    data: updateAudioFileDTO,
  };
  return await secureApiCallTemplate(config);
};

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export const getWorkspaceTags = async (workspaceUUID) => {
  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/tags`,
    method: "GET",
  };
  return await secureApiCallTemplate(config, true); // always fresh; store caches
};

export const attachTag = async (workspaceUUID, audioFileUUID, payload) => {
  // payload is { tagUUID } (existing) or { name } (create-if-missing + attach)
  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/tags`,
    method: "POST",
    data: payload,
  };
  return await secureApiCallTemplate(config);
};

export const detachTag = async (workspaceUUID, audioFileUUID, tagUUID) => {
  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/tags/${tagUUID}`,
    method: "DELETE",
  };
  return await secureApiCallTemplate(config);
};

export const createTempAudioFile = async (
  workspaceUUID,
  createTempAudioFileDTO,
) => {
  console.log("Called the createTempAudioFile method");

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/temporary`,
    method: "POST",
    data: createTempAudioFileDTO,
  };
  return await secureApiCallTemplate(config);
};

export const getUser = async () => {
  console.log("Called the getUser method");

  const config = {
    url: `${API_SERVER_URL}/user/byAuth0Id`,
    method: "GET",
  };
  return await secureApiCallTemplate(config);
};

export const initChat = async (workspaceUUID, audioFileUUID) => {
  console.log("Initialize the Chat by getting a Token and the history:");

  const config = {
    url: `${API_SERVER_URL}/chat/init`,
    method: "POST",
    data: {
      workspaceUUID,
      audioFileUUID,
    },
  };
  return await secureApiCallTemplate(config);
};

export const preloadChatSuggestions = async (audioFileUUID) => {
  console.log("Preloading AI chat suggestions for:", audioFileUUID);

  const config = {
    url: `${API_SERVER_URL}/chat/preload-suggestions`,
    method: "POST",
    data: {
      audioFileUUID,
    },
  };
  return await secureApiCallTemplate(config);
};

export const sendChatMessage = async (
  workspaceUUID,
  audioFileUUID,
  message,
) => {
  console.log("Initialize the Chat by getting a Token and the history:");

  const config = {
    url: `${API_SERVER_URL}/chat/message`,
    method: "POST",
    data: {
      workspaceUUID,
      audioFileUUID,
      message,
    },
  };
  return await secureApiCallTemplate(config);
};

export const deleteAudioFile = async (workspaceUUID, audioFileUUID) => {
  console.log(
    `Deleting an audio file with UUID ${audioFileUUID} and workspace UUID ${workspaceUUID}`,
  );

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}`,
    method: "DELETE",
    data: {
      workspaceUUID,
      audioFileUUID,
    },
  };
  return await secureApiCallTemplate(config);
};

export const downloadAudioFile = async (workspaceUUID, audioFileUUID) => {
  console.log("Initialize the Chat by getting a Token and the history:");

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/download`,
    method: "GET",
    data: {
      workspaceUUID,
      audioFileUUID,
    },
  };
  return await secureApiCallTemplate(config);
};

export const createCheckoutSession = async (billingPeriod, promoCode) => {
  console.log("Create a Checkout Session");

  const config = {
    url: `${API_SERVER_URL}/payment/create-checkout-session`,
    method: "POST",
    data: {
      billingPeriod,
      promoCode,
    },
  };
  return await secureApiCallTemplate(config);
};

export const isSubscribed = async () => {
  console.log("Check if the user is subscribed");

  const config = {
    url: `${API_SERVER_URL}/payment/is-subscribed`,
    method: "POST",
  };

  return await secureApiCallTemplate(config);
};

export const getSettingsUser = async () => {
  console.log("Get the user information");

  const config = {
    url: `${API_SERVER_URL}/payment/user`,
    method: "GET",
  };

  return await secureApiCallTemplate(config);
};

export const deleteUserAccount = async (reason, feedback) => {
  console.log("Deleting user account");

  const config = {
    url: `${API_SERVER_URL}/user/me`,
    method: "DELETE",
    data: {
      confirmed: true,
      reason: reason || undefined,
      feedback: feedback || undefined,
    },
  };

  return await secureApiCallTemplate(config);
};

export const updateSettingsUser = async (
  firstName,
  lastName,
  emailPreference,
) => {
  console.log(
    `Update the user information with name ${name} and emailPreference ${emailPreference}`,
  );

  const config = {
    url: `${API_SERVER_URL}/payment/user`,
    method: "POST",
    data: {
      firstName,
      lastName,
      emailPreference,
    },
  };

  return await secureApiCallTemplate(config);
};

export const createCustomerPortalSession = async () => {
  console.log("Create a Checkout Session");

  const config = {
    url: `${API_SERVER_URL}/payment/create-customer-portal-session`,
    method: "POST",
  };
  return await secureApiCallTemplate(config);
};

export const downloadTranscript = async (workspaceUUID, audioFileUUID) => {
  console.log("Download Transcript");

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/transcript`,
    method: "GET",
    data: {
      workspaceUUID,
      audioFileUUID,
    },
  };
  return await secureApiCallTemplate(config);
};

export const downloadTranscriptByFormat = async (
  workspaceUUID,
  audioFileUUID,
  format,
) => {
  console.log(
    `Downloading transcript for audioFileUUID: ${audioFileUUID} in format: ${format}`,
  );

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/transcript/download`,
    method: "GET",
    params: {
      format,
    },
    responseType: "blob", // Ensure the response is treated as a file
  };
  // Never from the GET cache: the transcript can change (edits, speaker
  // names) within the cache's five minutes.
  return await secureApiCallTemplate(config, true);
};

/**
 * Advanced transcript export (Otter-style modal). Sends the full ExportRequest
 * body and receives a file blob. The backend is the single source of truth for
 * all formats; a 409 (`error.status === 409`) means the transcript changed since
 * the preview was prepared and the caller should refresh and retry.
 *
 * @param {string} workspaceUUID
 * @param {string} audioFileUUID
 * @param {object} requestBody - matches the backend ExportRequestDto
 * @returns {Promise<{data: Blob|null, error: {message: string, status?: number}|null}>}
 */
export const exportTranscript = async (
  workspaceUUID,
  audioFileUUID,
  requestBody,
  signal,
) => {
  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/exports`,
    method: "POST",
    data: requestBody,
    responseType: "blob", // every format streams back as a blob
  };
  // Optional AbortSignal lets callers cancel superseded requests (e.g. rapid
  // subtitle-preview format switches).
  if (signal) config.signal = signal;
  return await secureApiCallTemplate(config, true);
};

/**
 * Bundle 2+ selected TEXT artifacts (transcript/summary/chat) into a single
 * .zip. Media is never bundled (it downloads separately from GCS). A 409 means
 * the transcript changed and the caller should refresh and retry.
 *
 * @param {string} workspaceUUID
 * @param {string} audioFileUUID
 * @param {object} requestBody - matches the backend ExportArchiveRequestDto
 * @returns {Promise<{data: Blob|null, error: {message: string, status?: number}|null}>}
 */
export const exportArchive = async (
  workspaceUUID,
  audioFileUUID,
  requestBody,
) => {
  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/exports/archive`,
    method: "POST",
    data: requestBody,
    responseType: "blob",
  };
  return await secureApiCallTemplate(config, true);
};

export const downloadSummary = async (workspaceUUID, audioFileUUID) => {
  console.log(`Downloading summary for audioFileUUID: ${audioFileUUID}`);

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/summary/download`,
    method: "GET",
    params: {
      format: "txt",
    },
    responseType: "blob", // Ensure the response is treated as a file
  };
  return await secureApiCallTemplate(config);
};

export const downloadChatHistory = async (workspaceUUID, audioFileUUID) => {
  console.log(`Downloading chat history for audioFileUUID: ${audioFileUUID}`);

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}/audio-file/${audioFileUUID}/chat/download`,
    method: "GET",
    params: {
      format: "txt",
    },
    responseType: "blob", // Ensure the response is treated as a file
  };
  // Never from the GET cache: new messages arrive within its five minutes.
  return await secureApiCallTemplate(config, true);
};

export const hasUserExceededFileUploadLimit = async (workspaceUUID) => {
  console.log("Check if the user has exceeded the file limit");
  console.log("workspaceUUID from hasExceededFileUploadLimit", workspaceUUID);
  const config = {
    url: `${API_SERVER_URL}/daily-file-limit`,
    method: "GET",
  };
  return await secureApiCallTemplate(config);
};

export const updateWorkspace = async (workspaceUUID, name) => {
  console.log(`Updating workspace with UUID ${workspaceUUID} and name ${name}`);

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}`,
    method: "PATCH",
    data: {
      name,
    },
  };
  return await secureApiCallTemplate(config);
};

export const deleteWorkspace = async (workspaceUUID) => {
  console.log(`Deleting workspace with UUID ${workspaceUUID}`);

  const config = {
    url: `${API_SERVER_URL}/workspace/${workspaceUUID}`,
    method: "DELETE",
  };
  return await secureApiCallTemplate(config);
};

export const secureApiCallTemplate = async (
  configParams,
  skipCache = false,
) => {
  //const { getAccessTokenSilently } = useAuth0();
  //const { loginWithRedirect } = useAuth0();
  // If getAccessTokenSilentlyOutsideComponent doesn't redirect (e.g. a
  // non-auth error), its rejection propagates so callers can handle it.
  const accessToken = await getAccessTokenSilentlyOutsideComponent();

  const config = {
    ...configParams,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };
  const { data, error } = await callExternalApi({ config, skipCache });

  return {
    data: data || null,
    error,
  };
};

// Token caching
let cachedToken = null;
let tokenExpiresAt = 0;
const TOKEN_REFRESH_THRESHOLD = 60 * 1000; // Refresh if expiring in less than 1 minute

export async function getAccessTokenSilentlyOutsideComponent(options) {
  const now = Date.now();

  // If token exists and isn't near expiration, use it
  if (cachedToken && now < tokenExpiresAt - TOKEN_REFRESH_THRESHOLD) {
    console.log("[Token] Using cached token");
    return cachedToken;
  }

  // Token doesn't exist or is expiring soon, get a new one
  try {
    console.log("[Token] Fetching new token");
    // Check if auth0 is available (not in test environment)
    if (!auth0) {
      throw new Error("Auth0 client not initialized");
    }
    const token = await auth0.getAccessTokenSilently(options);

    // Parse the JWT to get the expiration time
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    const payload = JSON.parse(jsonPayload);
    const expiresAt = payload.exp * 1000; // Convert to milliseconds

    // Cache the token
    cachedToken = token;
    tokenExpiresAt = expiresAt;

    console.log(
      `[Token] New token cached, expires in ${Math.round(
        (expiresAt - now) / 1000 / 60,
      )} minutes`,
    );
    return token;
  } catch (e) {
    console.error("[Token] Error fetching token:", e);

    // Clear our local token cache on any error
    cachedToken = null;
    tokenExpiresAt = 0;

    // Detect auth errors that indicate a stale/invalid session.
    // These occur when refresh tokens expire (idle timeout), get rotated out
    // (race condition during concurrent requests), or are otherwise invalid.
    // The only recovery is to clear the stale cache and force a fresh login.
    const errorMsg = (e.message || "").toLowerCase();
    const errorCode = (e.error || "").toLowerCase();
    const isAuthError =
      errorMsg.includes("missing refresh token") ||
      errorMsg.includes("unknown or invalid refresh token") ||
      errorMsg.includes("invalid refresh token") ||
      errorCode === "login_required" ||
      errorCode === "consent_required" ||
      errorCode === "invalid_grant";

    if (isAuthError) {
      console.warn(
        "[Token] Auth session invalid. Clearing cache and redirecting to login...",
        { error: e.error, message: e.message },
      );
      clearAuth0Cache();
      await auth0.loginWithRedirect();
      return; // Won't reach here — page navigates away
    }

    // For non-auth errors (network issues, etc.), throw so callers can handle
    throw e;
  }
}
